<template>
  <div ref="detalleDocumento" class="container-fluid"
      v-if="esquema.propiedades && registroSeleccionado.value">
      <div class="card">
        <div class="card-header pb-0">
          <div v-if="!hideDocumentTab" class="d-flex align-items-center justify-content-between">
            <div class="d-flex align-items-center gap-2">
              <img 
                :src="getFileIcon()" 
                :alt="getFileIconAlt()"
                class="file-icon-header"
                style="width: 36px; height: 36px;"
              />
              <p class="mb-0">{{ filename }}</p>
            </div>
            <button class="btn btn-sm btn-outline-primary ms-2" 
                    @click="abrirDocumentoEnNuevaPestana"
                    title="Abrir en nueva pestaña">
              <i class="fas fa-external-link-alt"></i>
            </button>
          </div>
          <ul class="nav nav-tabs">
            <li v-if="!hideDocumentTab" class="nav-item">
              <a class="nav-link" :class="{ active: tabActiva === 0 }" @click="tabActiva = 0">Documento</a>
            </li>
            <li class="nav-item">
              <a class="nav-link" :class="{ active: tabActiva === 1 }" @click="tabActiva = 1">Propiedades</a>
            </li>
            <li class="nav-item" v-if="hayAcciones">
              <a class="nav-link" :class="{ active: tabActiva === 2 }" @click="irATabAcciones">Acciones</a>
            </li>
            <li class="nav-item dropdown" style="max-width: 160px !important;" ref="dropdownOtros">
              <a class="nav-link dropdown-toggle" :class="{ active: tabActiva === 3 }" data-bs-toggle="dropdown"
                role="button" ref="dropdownToggle">Otros</a>
              <ul class="dropdown-menu" style="max-width: 160px !important;">
                <li><a class="dropdown-item" style="max-width: 160px !important;"
                    @click="seleccionarOtros(0)">Información</a></li>
                <li v-if="esquema.ocr == 1"><a class="dropdown-item" style="max-width: 160px !important;"
                    @click="seleccionarOtros(1)">Contenido</a></li>
                <li v-if="esquema.versionado == 1"><a class="dropdown-item" style="max-width: 160px !important;"
                    @click="seleccionarOtros(2)">Versiones</a></li>
                <li v-if="esquema.documentos_relacionados == 1"><a class="dropdown-item"
                    style="max-width: 160px !important;" @click="seleccionarOtros(3)">Doc. Relacionados</a></li>
                <li v-if="esquema.firmar_documentos == 1"><a class="dropdown-item"
                    style="max-width: 160px !important;" @click="seleccionarOtros(4)">Firmar documento</a></li>
                <li v-if="esquema.firmar_documentos_vidsigner == 1"><a class="dropdown-item"
                    style="max-width: 160px !important;" @click="seleccionarOtros(5)">Firmar con VidSigner</a></li>
              </ul>
            </li>
          </ul>
        </div>
        <div class="card-body">
          <!-- Contenido de las pestañas -->
          <div v-show="!hideDocumentTab && tabActiva === 0" ref="fullscreenDiv">
            <!-- Indicador de carga -->
            <div v-if="cargandoArchivo" class="text-center my-5">
              <div class="spinner-border text-primary" role="status" style="width: 3rem; height: 3rem;">
                <span class="visually-hidden">Cargando archivo...</span>
              </div>
              <p class="mt-3 text-muted">Cargando archivo, por favor espere...</p>
            </div>
            
            <!-- Visualización del contenido -->
            <div v-else-if="fileType === 'pdf'"
              :class="['pdf-container', { 'pdf-container-completa': isFullscreen }]">
              <vue-office-pdf v-if="pdfSrc" :key="`pdf-${fileKey}-${isFullscreen}`" :src="pdfSrc"
                :style="{ 
                  width: isFullscreen ? '100vw' : '100%', 
                  height: isFullscreen ? '100vh' : '70vh',
                  display: 'block'
                }" />
            </div>

            <div v-else-if="fileType === 'image'" class="image-container"
              :style="{ height: isFullscreen ? '100dvh' : 'auto' }">
              <img :src="imgbase64" alt="Vista previa del archivo" style="max-height: 100%; width: auto;" />
            </div>
            <div v-else-if="fileType === 'text'" class="file-container">
              <pre
                style="white-space: pre-wrap; font-family: inherit; background: none; border: 1px solid #e9ecef; border-radius: 6px; padding: 12px; min-height: 50vh;">{{ textContent }}</pre>
            </div>
            <vue-office-docx v-else-if="fileType === 'docx'" :src="docxSrc"
              :style="{ height: isFullscreen ? '100dvh' : '70vh' }" />
            <vue-office-excel v-else-if="fileType === 'xlsx'" :src="excelSrc"
              :style="{ height: isFullscreen ? '100dvh' : '70vh' }" />
            <div v-else-if="fileType === 'pptx'"
              :class="['pptx-container', { 'pptx-container-completa': isFullscreen }]">
              <vue-office-pptx v-if="pptxSrc" :key="`pptx-${fileKey}-${isFullscreen}`" :src="pptxSrc"
                :style="{ 
                  width: isFullscreen ? '100vw' : '100%', 
                  height: isFullscreen ? '100vh' : '70vh',
                  display: 'block'
                }" />
            </div>
            <div v-else-if="fileType === 'dwg'"
              style="min-height: 100px; min-width: 100px;"
              :class="['dwg-container', { 'dwg-container-completa': isFullscreen }]"
              :style="{ 
                width: isFullscreen ? '100vw' : '100%', 
                height: isFullscreen ? '100vh' : '70vh',
                display: 'block',
                position: 'relative'
              }">
              <VisorCAD2 
                :base64="dwgBase64 || ''" 
                :is-fullscreen="isFullscreen"
                @error="(msg) => showNotification(msg, 'alert-danger')"
              />
            </div>
            <div v-else class="file-container">
              <p>El archivo no se puede previsualizar. Descárgalo <a @click="descargarArchivo">haciendo clic
                  aquí</a>.
              </p>
            </div>
            <div class="d-flex justify-content-between">
              <button class="btn btn-secondary btn-sm mb-0" @click="descargarArchivo">Descargar
                documento</button>
              <button class="btn btn-secondary btn-sm mb-0" @click="pantallaCompleta">Pantalla completa</button>
            </div>
          </div>

          <!-- Pestaña de Propiedades -->
          <div v-show="tabActiva === 1">
            <div class="col-12 ">
              <div class="accordion" id="accordionPropiedades" :class="{ 'accordion-dropdown-open': openListCampo }">
                <div v-for="(grupo, index) in propiedadesAgrupadas" :key="'grupo_' + grupo.nombre" class="accordion-item">
                  <h2 class="accordion-header" :id="'heading' + index">
                    <button class="accordion-button" type="button" data-bs-toggle="collapse" 
                      :data-bs-target="'#collapse' + index" aria-expanded="true" 
                      :aria-controls="'collapse' + index">
                      {{ grupo.nombre || 'Sin grupo' }}
                      <small class="text-muted ms-2">({{ grupo.propiedades.length }})</small>
                    </button>
                  </h2>
                  <div :id="'collapse' + index" class="accordion-collapse collapse show" 
                    :aria-labelledby="'heading' + index" data-bs-parent="#accordionPropiedades">
                    <div class="accordion-body">
                      <div v-for="propiedad in grupo.propiedades" :key="'ficha_' + propiedad.id" class="form-group mb-3">
                        <label class="form-control-label">{{ formatearNombrePropiedad(propiedad.nombre) }}</label>
                        <template v-if="propiedad.tipo === 'text'">
                          <input class="form-control" style="min-height: 45px" type="text"
                            :value="registroSeleccionado.value[propiedad.campoBD]"
                            @input="updateField(propiedad.campoBD, $event.target.value)"
                            :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'longtext'">
                          <textarea class="form-control" style="min-height: 100px"
                            :value="registroSeleccionado.value[propiedad.campoBD]"
                            @input="updateField(propiedad.campoBD, $event.target.value)"
                            :disabled="esquema.editable === false"></textarea>
                        </template>
                        <template v-else-if="propiedad.tipo === 'int'">
                          <input class="form-control" style="min-height: 45px" type="number" step="1"
                            :value="registroSeleccionado.value[propiedad.campoBD]"
                            @input="updateField(propiedad.campoBD, parseInt($event.target.value) || 0)"
                            :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'decimal'">
                          <input class="form-control" style="min-height: 45px" type="number"
                            :value="parseFloat(registroSeleccionado.value[propiedad.campoBD])"
                            @input="updateDecimal(propiedad.campoBD, $event, 8)" :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'decimal2'">
                          <input class="form-control" style="min-height: 45px" type="number" step="0.01"
                            :value="formatDecimal(registroSeleccionado.value[propiedad.campoBD], 2)"
                            @input="updateDecimal(propiedad.campoBD, $event, 2)" :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'decimal4'">
                          <input class="form-control" style="min-height: 45px" type="number" step="0.0001"
                            :value="formatDecimal(registroSeleccionado.value[propiedad.campoBD], 4)"
                            @input="updateDecimal(propiedad.campoBD, $event, 4)" :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'date'">
                          <input class="form-control" style="min-height: 45px" type="date"
                            :value="registroSeleccionado.value[propiedad.campoBD]"
                            @input="updateField(propiedad.campoBD, $event.target.value)"
                            :disabled="esquema.editable === false">
                        </template>
                        <template v-else-if="propiedad.tipo === 'list'">
                          <div v-if="esquema.editable === false" class="form-control" style="min-height: 45px">
                            {{ registroSeleccionado.value[propiedad.campoBD] || '-' }}
                          </div>
                          <div v-else class="position-relative" :class="{ 'list-dropdown-open': openListCampo === propiedad.campoBD }">
                            <div class="form-control d-flex align-items-center justify-content-between"
                              style="min-height: 45px; cursor: pointer;"
                              @click="toggleListDropdown(propiedad)">
                              <span class="text-truncate">{{ registroSeleccionado.value[propiedad.campoBD] || 'Selecciona una opción' }}</span>
                              <i class="fas fa-chevron-down ms-2 flex-shrink-0"></i>
                            </div>
                            <div v-show="openListCampo === propiedad.campoBD"
                              :data-list-dropdown="propiedad.campoBD"
                              class="border rounded shadow-sm position-absolute start-0 end-0 mt-1 bg-white"
                              style="z-index: 1200; max-height: 280px;">
                              <input type="text"
                                class="form-control form-control-sm m-2"
                                placeholder="Buscar..."
                                v-model="listFilterText"
                                @keydown.escape="openListCampo = null"
                                @click.stop>
                              <ul class="list-group list-group-flush" style="max-height: 220px; overflow-y: auto;">
                                <li v-for="valor in valoresListaFiltrados(propiedad)" :key="valor.id"
                                  class="list-group-item list-group-item-action py-2"
                                  @click="selectValorLista(propiedad, valor)">
                                  {{ valor.descripcion }}
                                </li>
                                <li v-if="valoresListaFiltrados(propiedad).length === 0" class="list-group-item text-muted small">Sin resultados</li>
                              </ul>
                            </div>
                          </div>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="d-flex align-items-center mt-4">
                <button v-if="esquema.editable" class="btn btn-primary btn-sm ms-auto"
                  @click="guardarRegistro">Guardar</button>
              </div>
            </div>
          </div>

          <!-- Pestaña de Acciones -->
          <div v-if="hayAcciones" v-show="tabActiva === 2">
            <div class="d-flex flex-column gap-3 mt-4">
              <button class="btn btn-secondary w-100 d-flex align-items-center justify-content-center"
                @click="copiarEnlace">
                <i class="fas fa-link me-2"></i>
                Copiar enlace
              </button>
              <button v-if="esquema.borrable" class="btn btn-danger w-100"
                @click="mostrarModalEliminar = true">Eliminar</button>
              <button v-if="esquema.ocr == 1"
                class="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                @click="accionGenerarContenido" :disabled="cargandoGenerarContenido">
                <span v-if="cargandoGenerarContenido" class="spinner-border spinner-border-sm me-2"
                  role="status" aria-hidden="true"></span>
                Generar Contenido
              </button>
              <button v-if="esquema.adjuntable"
                class="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                @click="mostrarModalRemplazar = true">
                {{ esquema.versionado == 1 ? 'Crear Versión' : 'Remplazar Documento' }}
              </button>
              <button v-if="esquema.adjuntable && esquema.plantilla_word && esquema.plantillas_word_disponible"
                class="btn btn-success w-100 d-flex align-items-center justify-content-center"
                @click="generarDesdePlantilla" :disabled="generandoDocumento">
                <span v-if="generandoDocumento" class="spinner-border spinner-border-sm me-2"
                  role="status" aria-hidden="true"></span>
                <i v-else class="fas fa-file-word me-2"></i>
                {{ generandoDocumento ? 'Generando...' : 'Generar documento desde plantilla' }}
              </button>
              <template v-if="aprobacionPendiente?.puede_aprobar">
                <div class="border-top pt-2 mt-1 w-100" style="border-color: var(--bs-border-color) !important;"></div>
                <button type="button" class="btn btn-success w-100"
                  @click="aprobarDesdeDetalle" :disabled="procesandoAprobacionDetalle">
                  <span v-if="procesandoAprobacionDetalle" class="spinner-border spinner-border-sm me-1"></span>
                  Aprobar
                </button>
                <button type="button" class="btn btn-danger w-100"
                  @click="abrirModalRechazarDetalle" :disabled="procesandoAprobacionDetalle">
                  Rechazar
                </button>
              </template>
            </div>
          </div>

          <!-- Pestaña de Otros -->
          <div v-show="tabActiva === 3">
            <div v-show="subTabActiva === 0" class="mt-2">
              <div class="row g-3">
                <div class="col-12 d-flex justify-content-between">
                  <span class="text-secondary">Nombre</span>
                  <strong>{{ filename }}</strong>
                </div>
                <div class="col-12 d-flex justify-content-between">
                  <span class="text-secondary">Fecha de subida</span>
                  <strong>{{ formatDateTime(registroSeleccionado.value?.created_at) || '-' }}</strong>
                </div>
                <div class="col-12 d-flex justify-content-between">
                  <span class="text-secondary">Fecha de actualización</span>
                  <strong>{{ formatDateTime(registroSeleccionado.value?.updated_at) || '-' }}</strong>
                </div>
                
                <!-- Información de Aprobación -->
                <div v-if="esquema.requiere_aprobacion" class="col-12 mt-3">
                  <hr>
                  <h6 class="mb-3">Estado de Aprobación</h6>
                  <div v-if="registroSeleccionado.value?.aprobado" class="estado-aprobacion estado-aprobado d-flex align-items-center gap-2">
                    <i class="fas fa-check-circle"></i>
                    <div class="flex-grow-1">
                      <strong>Documento Aprobado</strong>
                      <div v-if="registroSeleccionado.value?.aprobado_por_nombre" class="small">
                        Aprobado por: {{ registroSeleccionado.value.aprobado_por_nombre }}
                      </div>
                      <div v-if="registroSeleccionado.value?.aprobado_en" class="small">
                        Fecha: {{ formatDateTime(registroSeleccionado.value.aprobado_en) }}
                      </div>
                    </div>
                  </div>
                  <div v-else-if="registroSeleccionado.value?.rechazado" class="estado-aprobacion estado-rechazado d-flex align-items-center gap-2">
                    <i class="fas fa-times-circle"></i>
                    <div class="flex-grow-1">
                      <strong>Documento Rechazado</strong>
                      <div v-if="registroSeleccionado.value?.rechazado_por_nombre" class="small">
                        Rechazado por: {{ registroSeleccionado.value.rechazado_por_nombre }}
                      </div>
                      <div v-if="registroSeleccionado.value?.rechazado_en" class="small">
                        Fecha: {{ formatDateTime(registroSeleccionado.value.rechazado_en) }}
                      </div>
                      <div v-if="registroSeleccionado.value?.motivo_rechazo" class="small mt-2">
                        <strong>Motivo:</strong> {{ registroSeleccionado.value.motivo_rechazo }}
                      </div>
                    </div>
                  </div>
                  <div v-else class="estado-aprobacion estado-pendiente d-flex align-items-center gap-2">
                    <i class="fas fa-clock"></i>
                    <strong>Pendiente de Aprobación</strong>
                  </div>
                </div>
                
                <!-- Información de Caducidad -->
                <div v-if="esquema.tiene_caducidad && propiedadCaducidad" class="col-12 mt-3">
                  <hr>
                  <h6 class="mb-3">Caducidad</h6>
                  <div class="d-flex justify-content-between align-items-center">
                    <span class="text-secondary">Fecha de caducidad:</span>
                    <strong>{{ formatDate(registroSeleccionado.value?.[propiedadCaducidad.campoBD]) || 'No definida' }}</strong>
                  </div>
                  <div v-if="diasRestantesCaducidad !== null" class="mt-2">
                    <span v-if="diasRestantesCaducidad < 0" class="badge bg-danger">
                      Caducado hace {{ Math.abs(diasRestantesCaducidad) }} días
                    </span>
                    <span v-else-if="diasRestantesCaducidad === 0" class="badge bg-warning">
                      Caduca hoy
                    </span>
                    <span v-else-if="diasRestantesCaducidad <= 7" class="badge bg-warning">
                      Caduca en {{ diasRestantesCaducidad }} días
                    </span>
                    <span v-else-if="diasRestantesCaducidad <= 30" class="badge bg-info">
                      Caduca en {{ diasRestantesCaducidad }} días
                    </span>
                    <span v-else class="badge bg-success">
                      Caduca en {{ diasRestantesCaducidad }} días
                    </span>
                  </div>
                </div>
              </div>
              <div class="col-12 mt-4">
                <span class="text-secondary">Actividad</span>
                  <div v-if="cargandoLogs" class="text-center my-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
              </div>
              <div v-else>
                <ul class="list-group">
                  <li v-if="!logs || logs.length === 0" class="list-group-item">Sin actividad</li>
                  <li v-for="(log, idx) in logs" :key="'ver_' + idx"
                    class="list-group-item d-flex justify-content-between align-items-center">
                    <div class="d-flex flex-column w-100">
                      <div class="d-flex flex-row justify-content-between w-100">
                        <strong>{{log.accion}}</strong>
                        <small class="text-secondary">{{ log.usuario_name }} {{ formatDateTime(log.created_at) || '-' }}</small>
                      </div>
                      <small class="text-secondary">{{ log.detalles }}</small>
                    </div>
                  </li>
                </ul>
              </div>
                </div>
              
            </div>
            <div v-if="esquema.ocr == 1" v-show="subTabActiva === 1">
              <div v-if="cargandoOcr" class="text-center my-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
              </div>
              <div v-else class="ocr-content-scroll">
                <pre
                  style="white-space: pre-wrap; font-family: inherit; background: none; border: none; margin: 0;">
          {{ textoOcr }}</pre>
              </div>
            </div>
            <div v-if="esquema.versionado == 1" v-show="subTabActiva === 2" class="mt-2">
              <div v-if="cargandoVersiones" class="text-center my-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Cargando...</span>
                </div>
              </div>
              <div v-else>
                <ul class="list-group">
                  <li v-if="!versiones || versiones.length === 0" class="list-group-item">Sin versiones
                    disponibles</li>
                  <li v-for="(ver, idx) in versiones" :key="'ver_' + idx"
                    class="list-group-item d-flex justify-content-between align-items-center">
                    <div class="d-flex flex-column">
                      <strong>Versión {{ ver.numero || idx + 1 }}</strong>
                      <small class="text-secondary">{{ ver.fecha || '-' }}</small>
                    </div>
                    <div class="d-flex align-items-center">
                      <button class="btn btn-sm btn-secondary" @click="descargarVersion(ver)">Descargar</button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div v-if="esquema.documentos_relacionados == 1" v-show="subTabActiva === 3" class="mt-2">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <h4 class="mb-0 h-100">Documentos Relacionados</h4>
                <button class="btn btn-primary btn-sm mb-0" @click="mostrarModalRelacionar = true">Añadir</button>
              </div>
              <div v-if="cargandoRelacionados" class="text-center my-3">
                <div class="spinner-border text-primary" role="status">
                  <span class="visualmente-hidden"></span>
                </div>
              </div>
              <div v-else>
                <ul class="list-group">
                  <li v-if="!documentosRelacionados || documentosRelacionados.length === 0"
                    class="list-group-item">Sin
                    documentos relacionados</li>
                  <li v-for="(doc, idx) in documentosRelacionados" :key="'rel_' + idx"
                    class="list-group-item d-flex justify-content-between align-items-center">
                    <div class="d-flex flex-column">
                      <strong>{{ doc.nombre || ('Documento ' + (idx + 1)) }}</strong>
                      <small class="text-secondary">{{ formatDateTime(doc.fecha || doc.created_at) || '-'
                        }}</small>
                      <small v-if="doc.esquema_id && doc.esquema_id !== esquemaId" 
                        class="text-info mt-1">
                        <i class="fas fa-folder"></i> {{ getEsquemaNombre(doc.esquema_id) }}
                      </small>
                    </div>
                    <div class="d-flex flex-column align-items-center gap-1">
                      <button class="btn btn-xs btn-primary w-100"
                        @click="irAVerDocumentoRelacionado(doc)"
                        title="Abrir en nueva pestaña">
                        <i class="fas fa-external-link-alt"></i>
                      </button>
                      <button class="btn btn-xs btn-secondary w-100"
                        @click="descargarDocumentoRelacionado(doc)"
                        title="Descargar">
                        <i class="fas fa-download"></i>
                      </button>
                      <button class="btn btn-xs btn-danger w-100"
                        @click="quitarDocumentoRelacionado(doc)"
                        title="Quitar">
                        <i class="fas fa-trash"></i>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <FirmarDocumento
              v-if="subTabActiva === 4"
              :key="`firmar-${registroSeleccionado.value?.id || id}`"
              :esquema="esquema"
              :registro-seleccionado="registroSeleccionado"
              :show-notification="showNotification"
              :id="id"
              @actualizar-registro="handleActualizarRegistro"
            />

            <FirmarDocumentoVidsigner
              v-if="subTabActiva === 5"
              :key="`firmar-vidsigner-${registroSeleccionado.value?.id || id}`"
              :esquema="esquema"
              :registro-seleccionado="registroSeleccionado"
              :show-notification="showNotification"
              :id="id"
              @actualizar-registro="handleActualizarRegistro"
            />

          </div>
        </div>
      </div>
    </div>

    <!-- Modales de Acciones -->
    <!-- Modal de confirmación para eliminar -->
    <div v-if="mostrarModalEliminar" class="modal fade show" tabindex="-1"
      style="display: block; background: rgba(0,0,0,0.3);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Confirmar eliminación</h5>
            <button type="button" class="btn-close" @click="mostrarModalEliminar = false"></button>
          </div>
          <div class="modal-body">
            <p>¿Estás seguro de que deseas eliminar este registro?</p>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="mostrarModalEliminar = false">Cancelar</button>
            <button class="btn btn-danger" @click="confirmarEliminar">Eliminar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Remplazar Documento -->
    <div v-if="mostrarModalRemplazar" class="modal fade show" tabindex="-1"
      style="display: block; background: rgba(0,0,0,0.3);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ esquema.versionado == 1 ? 'Crear Versión' : 'Remplazar Documento' }}</h5>
            <button type="button" class="btn-close" @click="cancelarRemplazar"></button>
          </div>
          <div class="modal-body">
            <input type="file" @change="onFileChangeRemplazar" class="form-control" />
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cancelarRemplazar">Cancelar</button>
            <button class="btn btn-primary" :disabled="!fileRemplazo" @click="guardarRemplazo">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Relacionar Documento -->
    <div v-if="mostrarModalRelacionar" class="modal fade show" tabindex="-1"
      style="display: block; background: rgba(0,0,0,0.3);"
      @click.self="cerrarRelacionar">
      <div class="modal-dialog modal-lg modal-relacionar">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Relacionar Documento</h5>
            <button type="button" class="btn-close" @click="cerrarRelacionar"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3" v-if="mostrarFiltroEsquema">
              <label class="form-label small">Filtrar por esquema (opcional):</label>
              <select class="form-select form-select-sm" v-model="esquemaBuscar">
                <option :value="null">Todos los esquemas</option>
                <option v-for="esq in esquemasParaRelacionar" :key="esq.id" :value="esq.id">
                  {{ esq.nombre }}
                </option>
              </select>
            </div>
            <div class="input-group mb-3 align-items-stretch">
              <input type="text" class="form-control" placeholder="Buscar..." v-model="busquedaTermino"
                @keyup.enter="buscarDocumentos">
              <button class="btn btn-outline-secondary mb-0" type="button" @click="buscarDocumentos"
                :disabled="cargandoBusqueda" style="min-width: 100px;">
                <span v-if="cargandoBusqueda" class="spinner-border spinner-border-sm" role="status"
                  aria-hidden="true"></span>
                Buscar
              </button>
            </div>
            <div class="table-responsive">
              <table class="table table-striped">
                <thead>
                  <tr>
                    <th>Nombre</th>
                    <th>Fecha</th>
                    <th>Esquema</th>
                    <th class="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(res, idx) in resultadosBusqueda" :key="'res_' + idx">
                    <td>{{ res.nombre || ('Documento ' + (idx + 1)) }}</td>
                    <td>{{ formatDateTime(res.created_at) || '-' }}</td>
                    <td>
                      <span v-if="res.esquema_id" class="badge bg-info">
                        {{ getEsquemaNombre(res.esquema_id) }}
                      </span>
                      <span v-else class="text-muted">-</span>
                    </td>
                    <td class="text-end">
                      <button class="btn btn-sm btn-primary" @click="relacionarDocumento(res)"
                        :disabled="relacionando">Seleccionar</button>
                    </td>
                  </tr>
                  <tr v-if="!resultadosBusqueda || resultadosBusqueda.length === 0">
                    <td colspan="4" class="text-center text-secondary">Sin resultados</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="cerrarRelacionar">Cerrar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal rechazar documento (desde detalle) -->
    <div v-if="mostrarModalRechazarDetalle" class="modal fade show" tabindex="-1" style="display:block; background: rgba(0,0,0,0.4);">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Rechazar documento</h5>
            <button type="button" class="btn-close" @click="mostrarModalRechazarDetalle = false"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Motivo del rechazo <span class="text-danger">*</span></label>
              <textarea class="form-control" v-model="motivoRechazoDetalle" rows="4"
                placeholder="Describe el motivo del rechazo..." maxlength="1000"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="mostrarModalRechazarDetalle = false">Cancelar</button>
            <button class="btn btn-danger" @click="rechazarDesdeDetalle"
              :disabled="!motivoRechazoDetalle?.trim() || procesandoAprobacionDetalle">
              <span v-if="procesandoAprobacionDetalle" class="spinner-border spinner-border-sm me-2" role="status"></span>
              Rechazar
            </button>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { ref, computed, watch, nextTick, useTemplateRef, onMounted, onBeforeUnmount } from 'vue'
import { useRoute } from 'vue-router'
import VueOfficePdf from '@vue-office/pdf'
import VueOfficeDocx from '@vue-office/docx'
import VueOfficeExcel from '@vue-office/excel'
import VueOfficePptx from '@vue-office/pptx'
import '@vue-office/docx/lib/index.css'
import '@vue-office/excel/lib/index.css'
import axiosInstance from "@/axios"
import FirmarDocumento from "@/components/FirmarDocumento.vue"
import FirmarDocumentoVidsigner from "@/components/FirmarDocumentoVidsigner.vue"
import VisorCAD2 from "@/components/VisorCAD2.vue"

export default {
  name: 'DetalleDocumento',
  components: { VueOfficeDocx, VueOfficeExcel, VueOfficePdf, VueOfficePptx, FirmarDocumento, FirmarDocumentoVidsigner, VisorCAD2 },
  props: {
    esquema: {
      type: Object,
      required: true
    },
    registroSeleccionado: {
      type: Object,
      required: true
    },
    listasDisponibles: {
      type: Array,
      default: () => []
    },
    showNotification: {
      type: Function,
      required: true
    },
    id: {
      type: [String, Number],
      required: true
    },
    hideDocumentTab: {
      type: Boolean,
      default: false
    }
  },
  emits: ['guardar-registro', 'eliminar-registro', 'actualizar-registro'],
  setup(props, { emit }) {
    // Obtener la ruta para acceder al esquemaId como fallback
    const route = useRoute()
    
    // Computed para obtener el esquemaId (props.id o route.params?.id)
    const esquemaId = computed(() => props.id || route.params?.id)
    
    // Cache para valores de listas (listaId -> valores[])
    const valoresListasCache = ref({})
    // Peticiones en curso para no duplicar llamadas al mismo lista_id
    const valoresListasPending = ref({})
    // Selector de lista con búsqueda
    const openListCampo = ref(null)
    const listFilterText = ref('')
    let listOutsideClickHandler = null
    let listOutsideClickTimeout = null
    const clearListOutsideClick = () => {
      if (listOutsideClickTimeout) {
        clearTimeout(listOutsideClickTimeout)
        listOutsideClickTimeout = null
      }
      if (listOutsideClickHandler) {
        document.removeEventListener('click', listOutsideClickHandler)
        listOutsideClickHandler = null
      }
    }
    
    // Variables reactivas (si hideDocumentTab, empezar en Propiedades)
    const tabActiva = ref(props.hideDocumentTab ? 1 : 0)
    const subTabActiva = ref(0)
    const detalleDocumento = useTemplateRef('detalleDocumento')
    const fullscreenDiv = useTemplateRef('fullscreenDiv')
    const dropdownOtros = useTemplateRef('dropdownOtros')
    const isFullscreen = ref(false)
    const fileKey = ref(0)
    
    // Variables para el visor CAD
    const dwgBase64 = ref('')

    // Variables de archivo
    const fileUrl = ref('')
    const fileType = ref('')
    const filename = ref('')
    const imgbase64 = ref('')
    const textContent = ref('')
    const docxSrc = ref('')
    const excelSrc = ref('')
    const pptxSrc = ref('')
    const pdfSrc = ref('')

    // Variables de modales
    const mostrarModalEliminar = ref(false)
    const mostrarModalRemplazar = ref(false)
    const mostrarModalRelacionar = ref(false)
    const fileRemplazo = ref(null)
    const fileRemplazoBase64 = ref("")

    // Variables de carga
    const cargandoGenerarContenido = ref(false)
    const cargandoOcr = ref(false)
    const cargandoVersiones = ref(false)
    const cargandoRelacionados = ref(false)
    const cargandoBusqueda = ref(false)
    const cargandoLogs = ref(false)
    const cargandoArchivo = ref(false)
    const relacionando = ref(false)
    const generandoDocumento = ref(false)

    // Aprobar / Rechazar documento (info cargada al entrar en tab Acciones, solo la primera vez)
    const aprobacionInfo = ref(null)
    const permisosAprobacionCargadosEnAcciones = ref(false)
    const mostrarModalRechazarDetalle = ref(false)
    const motivoRechazoDetalle = ref('')
    const procesandoAprobacionDetalle = ref(false)

    // Variables de datos
    const textoOcr = ref('')
    const versiones = ref([])
    const documentosRelacionados = ref([])
    const resultadosBusqueda = ref([])
    const busquedaTermino = ref('')
    const busquedaPaginacion = ref({ page: 1, per_page: 10, total: 0 })
    const logs = ref([])
    const esquemasDisponibles = ref([]) // Lista de esquemas para mostrar nombres
    const esquemaBuscar = ref(null) // Esquema seleccionado para búsqueda

    // Computed properties
    const hayAcciones = computed(() => {
      return !!(props.esquema?.borrable || props.esquema?.ocr == 1 || props.esquema?.adjuntable)
    })

    const propiedadesAgrupadas = computed(() => {
      if (!props.esquema?.propiedades) return []
      
      const grupos = {}
      props.esquema.propiedades.forEach(propiedad => {
        const grupoNombre = propiedad.grupo || 'Sin grupo'
        if (!grupos[grupoNombre]) {
          grupos[grupoNombre] = {
            nombre: grupoNombre,
            propiedades: []
          }
        }
        grupos[grupoNombre].propiedades.push(propiedad)
      })
      
      const gruposArray = Object.values(grupos).sort((a, b) => {
        if (a.nombre === 'Sin grupo') return 1
        if (b.nombre === 'Sin grupo') return -1
        return a.nombre.localeCompare(b.nombre)
      })
      
      gruposArray.forEach(grupo => {
        grupo.propiedades.sort((a, b) => (a.orden || 0) - (b.orden || 0))
      })
      
      return gruposArray
    })

    // Esquemas disponibles para relacionar documentos (solo los que tienen documentos_relacionados = 1)
    const esquemasParaRelacionar = computed(() => {
      return esquemasDisponibles.value.filter(esq => esq.documentos_relacionados == 1)
    })

    // Mostrar filtro solo si hay más de un esquema disponible
    const mostrarFiltroEsquema = computed(() => {
      return esquemasParaRelacionar.value.length > 1
    })

    // Funciones de utilidad
    const formatearNombrePropiedad = (nombre) => {
      if (!nombre) return ''
      return nombre.replace(/_/g, ' ')
    }

    const formatDecimal = (value, digits) => {
      return value ? parseFloat(value).toFixed(digits) : ""
    }

    const formatDateTime = (value) => {
      if (!value) return ''
      const d = new Date(value)
      const pad = (n) => ("0" + n).slice(-2)
      const day = pad(d.getDate())
      const month = pad(d.getMonth() + 1)
      const year = d.getFullYear()
      const hours = pad(d.getHours())
      const minutes = pad(d.getMinutes())
      const seconds = pad(d.getSeconds())
      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    }

    const getFileTypeFromExtension = (extension) => {
      const ext = extension.toLowerCase().replace('.', '')
      if (ext === 'pdf') return 'pdf'
      if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return 'image'
      if (['txt', 'log', 'md'].includes(ext)) return 'text'
      if (['docx'].includes(ext)) return 'docx'
      if (['xlsx', 'csv', 'xls'].includes(ext)) return 'xlsx'
      if (['pptx'].includes(ext)) return 'pptx'
      if (['dwg', 'dxf'].includes(ext)) return 'dwg'
      return 'other'
    }

    const obtenerMimeDesdeExtension = (filename) => {
      const extension = filename.split('.').pop().toLowerCase()
      const mimeTypes = {
        pdf: 'application/pdf',
        png: 'image/png',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        gif: 'image/gif',
        svg: 'image/svg+xml',
        txt: 'text/plain',
        csv: 'text/csv',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        zip: 'application/zip',
        json: 'application/json',
        dwg: 'application/acad',
        dxf: 'image/vnd.dxf'
      }
      return mimeTypes[extension] || 'application/octet-stream'
    }

    // Función para obtener el icono del archivo según su extensión
    const getFileIcon = () => {
      // Intentar obtener la extensión del registro seleccionado
      let extension = ''
      
      if (props.registroSeleccionado.value?.extension) {
        extension = props.registroSeleccionado.value.extension.toLowerCase().replace('.', '')
      } else if (filename.value) {
        // Si no hay extensión directa, intentar extraerla del filename
        const partes = filename.value.split('.')
        if (partes.length > 1) {
          extension = partes[partes.length - 1].toLowerCase()
        }
      }

      // Mapeo de extensiones a iconos
      const iconMap = {
        'pdf': '/img/files/fi-pdf.svg',
        'jpg': '/img/files/fi-jpg.svg',
        'jpeg': '/img/files/fi-jpg.svg',
        'png': '/img/files/fi-jpg.svg',
        'gif': '/img/files/fi-jpg.svg',
        'bmp': '/img/files/fi-jpg.svg',
        'webp': '/img/files/fi-jpg.svg',
        'txt': '/img/files/fi-txt.svg',
        'csv': '/img/files/fi-csv.svg',
        'xlsx': '/img/files/fi-xlsx.svg',
        'xls': '/img/files/fi-xls.svg',
        'docx': '/img/files/fi-docx.svg',
        'doc': '/img/files/fi-doc.svg',
        'zip': '/img/files/fi-zip.svg',
        'json': '/img/files/fi-json.svg',
        'dwg': '/img/files/fi-other.svg',
        'dxf': '/img/files/fi-other.svg'
      }

      // Retornar el icono correspondiente o el icono por defecto
      return iconMap[extension] || '/img/files/any-file.png'
    }

    // Función para obtener el texto alternativo del icono
    const getFileIconAlt = () => {
      let extension = ''
      if (props.registroSeleccionado.value?.extension) {
        extension = props.registroSeleccionado.value.extension.toLowerCase().replace('.', '')
      } else if (filename.value) {
        const partes = filename.value.split('.')
        if (partes.length > 1) {
          extension = partes[partes.length - 1].toLowerCase()
        }
      }
      return extension ? `Archivo ${extension.toUpperCase()}` : 'Archivo'
    }

    const base64ToArrayBuffer = (base64) => {
      const binaryString = atob(base64)
      const len = binaryString.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes.buffer
    }

    const base64ToUint8Array = (base64) => {
      const binaryString = atob(base64)
      const len = binaryString.length
      const bytes = new Uint8Array(len)
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i)
      }
      return bytes
    }

    // Función para cargar valores de una lista de forma asíncrona (una sola petición por lista_id)
    const cargarValoresLista = async (listaId) => {
      if (!listaId || valoresListasCache.value[listaId]) return
      if (valoresListasPending.value[listaId]) return valoresListasPending.value[listaId]

      const promise = (async () => {
        try {
          const response = await axiosInstance.get(`/api/listas/${listaId}/valores`)
          const valores = response.data.data?.lista?.valores ?? response.data.lista?.valores ?? []
          valoresListasCache.value[listaId] = valores
          return valores
        } catch (error) {
          console.error(`Error al obtener valores de la lista ${listaId}:`, error)
          valoresListasCache.value[listaId] = []
          return []
        } finally {
          delete valoresListasPending.value[listaId]
        }
      })()
      valoresListasPending.value[listaId] = promise
      return promise
    }

    // Función para obtener valores de una lista (síncrona, usa cache)
    const obtenerValoresLista = (listaId) => {
      if (!listaId) return []

      // Si no están en cache, iniciar carga asíncrona
      if (!valoresListasCache.value[listaId]) {
        cargarValoresLista(listaId)
        return [] // Retornar vacío mientras carga
      }

      return valoresListasCache.value[listaId] || []
    }

    const valoresListaFiltrados = (propiedad) => {
      const valores = obtenerValoresLista(propiedad.lista_id)
      const q = (listFilterText.value || '').trim().toLowerCase()
      if (!q) return valores
      return valores.filter(v => (v.descripcion || '').toLowerCase().includes(q))
    }

    const toggleListDropdown = (propiedad) => {
      if (openListCampo.value === propiedad.campoBD) {
        openListCampo.value = null
        listFilterText.value = ''
        clearListOutsideClick()
        return
      }
      clearListOutsideClick()
      openListCampo.value = propiedad.campoBD
      listFilterText.value = ''
      nextTick(() => {
        const el = document.querySelector(`[data-list-dropdown="${propiedad.campoBD}"]`)
        const input = el?.querySelector('input')
        if (input) input.focus()
        const handler = (e) => {
          if (el && !el.contains(e.target)) {
            openListCampo.value = null
            listFilterText.value = ''
            clearListOutsideClick()
          }
        }
        listOutsideClickHandler = handler
        listOutsideClickTimeout = setTimeout(() => document.addEventListener('click', handler), 0)
      })
    }

    const selectValorLista = (propiedad, valor) => {
      updateField(propiedad.campoBD, valor.descripcion)
      openListCampo.value = null
      listFilterText.value = ''
      clearListOutsideClick()
    }

    watch(openListCampo, (val) => {
      if (!val) clearListOutsideClick()
    })

    // Funciones de actualización de campos
    const updateField = (campo, value) => {
      emit('actualizar-registro', { ...props.registroSeleccionado.value, [campo]: value })
    }

    // Funciones de actualización de decimales
    const updateDecimal = (campo, event, digits) => {
      // Usar valueAsNumber que devuelve el valor numérico directamente para inputs type="number"
      const parsedValue = event.target.valueAsNumber
      // Si el campo está vacío o no es un número válido, establecer null
      if (isNaN(parsedValue) || parsedValue === null || parsedValue === undefined) {
        emit('actualizar-registro', { ...props.registroSeleccionado.value, [campo]: null })
        return
      }
      // Redondear solo al final con el número de decimales especificado
      const multiplier = Math.pow(10, digits)
      const newValue = Math.round(parsedValue * multiplier) / multiplier
      emit('actualizar-registro', { ...props.registroSeleccionado.value, [campo]: newValue })
    }

    // Funciones de acciones
    const aprobacionPendiente = computed(() => aprobacionInfo.value)
    const esAprobacionAvanzada = () => (aprobacionPendiente.value?.tipo || '').toLowerCase() === 'aprobacion_avanzada'

    const cargarPermisosAprobacion = async () => {
      console.log('inicio cargarPermisosAprobacion')
      const reg = props.registroSeleccionado?.value
      const esquemaId = props.id || route.params?.id
      if (!reg?.id || !esquemaId) {
        console.log('cargarPermisosAprobacion: sin reg.id o esquemaId', { reg, esquemaId })
        aprobacionInfo.value = null
        return
      }
      console.log('cargarPermisosAprobacion: datos iniciales', {
        registroId: reg.id,
        esquemaId,
        aprobado: reg.aprobado,
        tipoAprobacion: reg.tipo_aprobacion || reg.tipo
      })
      // Solo cargar si el documento está pendiente (aprobado == 0)
      if (!(reg.aprobado === 0 || reg.aprobado === '0')) {
        console.log('cargarPermisosAprobacion: documento no pendiente, no se consulta permisos')
        aprobacionInfo.value = null
        return
      }
      try {
        console.log('cargarPermisosAprobacion: llamando a endpoint permisos', {
          url: `/api/aprobacion/permisos/${esquemaId}/${reg.id}`
        })
        const res = await axiosInstance.get(`/api/aprobacion/permisos/${esquemaId}/${reg.id}`)
        console.log('cargarPermisosAprobacion: respuesta recibida', res.data)
        aprobacionInfo.value = res.data?.data || res.data || null
      } catch (error) {
        console.error('Error al obtener permisos de aprobación:', error)
        aprobacionInfo.value = null
      }
    }

    const irATabAcciones = () => {
      tabActiva.value = 2
      if (!permisosAprobacionCargadosEnAcciones.value) {
        permisosAprobacionCargadosEnAcciones.value = true
        cargarPermisosAprobacion()
      }
    }

    const aprobarDesdeDetalle = async () => {
      const reg = props.registroSeleccionado?.value
      const esquemaId = props.id || route.params?.id
      if (!reg?.id || !esquemaId || !aprobacionPendiente.value?.puede_aprobar) return
      const url = esAprobacionAvanzada()
        ? `/api/aprobacionAvanzada/aprobar/${esquemaId}/${reg.id}`
        : `/api/aprobarDocumento/${esquemaId}/${reg.id}`
      procesandoAprobacionDetalle.value = true
      try {
        const res = await axiosInstance.post(url, esAprobacionAvanzada() ? {} : undefined)
        if (res.data?.success) {
          props.showNotification('Documento aprobado correctamente', 'alert-success')
          aprobacionInfo.value = null
          emit('actualizar-registro', { ...reg })
        }
      } catch (err) {
        props.showNotification(err.response?.data?.message || 'Error al aprobar el documento', 'alert-danger')
      } finally {
        procesandoAprobacionDetalle.value = false
      }
    }

    const abrirModalRechazarDetalle = () => {
      motivoRechazoDetalle.value = ''
      mostrarModalRechazarDetalle.value = true
    }

    const rechazarDesdeDetalle = async () => {
      const reg = props.registroSeleccionado?.value
      const esquemaId = props.id || route.params?.id
      if (!reg?.id || !esquemaId || !motivoRechazoDetalle.value?.trim() || !aprobacionPendiente.value?.puede_aprobar) return
      const url = esAprobacionAvanzada()
        ? `/api/aprobacionAvanzada/rechazar/${esquemaId}/${reg.id}`
        : `/api/rechazarDocumento/${esquemaId}/${reg.id}`
      const body = { motivo_rechazo: motivoRechazoDetalle.value.trim() }
      procesandoAprobacionDetalle.value = true
      try {
        const res = await axiosInstance.post(url, body)
        if (res.data?.success) {
          props.showNotification('Documento rechazado correctamente', 'alert-success')
          mostrarModalRechazarDetalle.value = false
          motivoRechazoDetalle.value = ''
          aprobacionInfo.value = null
          emit('actualizar-registro', { ...reg })
        }
      } catch (err) {
        props.showNotification(err.response?.data?.message || 'Error al rechazar el documento', 'alert-danger')
      } finally {
        procesandoAprobacionDetalle.value = false
      }
    }

    const guardarRegistro = async () => {
      emit('guardar-registro', props.registroSeleccionado.value)
    }

    const confirmarEliminar = async () => {
      mostrarModalEliminar.value = false
      emit('eliminar-registro', props.registroSeleccionado.value)
    }

    const accionGenerarContenido = async () => {
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) {
        props.showNotification('No hay documento seleccionado.', 'alert-danger')
        return
      }
      cargandoGenerarContenido.value = true
      try {
        const esquemaId = props.id || route.params?.id;
        await axiosInstance.post(`api/procesarOCR/${esquemaId}/${props.registroSeleccionado.value.id}`)
      } catch (error) {
        props.showNotification('Error al generar el contenido.', 'alert-danger')
      } finally {
        cargandoGenerarContenido.value = false
      }
    }

    const copiarEnlace = async () => {
      if (!props.registroSeleccionado.value?.id) return
      
      const esquemaId = props.id || route.params?.id;
      const url = `${window.location.origin}/esquema/${esquemaId}/registro/${props.registroSeleccionado.value.id}`
      
      try {
        await navigator.clipboard.writeText(url)
        props.showNotification('Enlace copiado al portapapeles', 'alert-success')
      } catch (error) {
        const textArea = document.createElement('textarea')
        textArea.value = url
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        props.showNotification('Enlace copiado al portapapeles', 'alert-success')
      }
    }

    const handleActualizarRegistro = (registroActualizado) => {
      emit('actualizar-registro', registroActualizado)
    }

    const abrirDocumentoEnNuevaPestana = () => {
      if (!props.registroSeleccionado.value?.id) return
      
      const esquemaId = props.id || route.params?.id;
      const url = `/esquema/${esquemaId}/registro/${props.registroSeleccionado.value.id}`
      window.open(url, '_blank')
    }

    const descargarArchivo = () => {
      const mime = obtenerMimeDesdeExtension(filename.value)
      const enlace = document.createElement('a')
      enlace.href = `data:${mime};base64,` + fileUrl.value
      enlace.download = filename.value
      document.body.appendChild(enlace)
      enlace.click()
      document.body.removeChild(enlace)
    }

    const pantallaCompleta = () => {
      const elem = fullscreenDiv.value

      if (!document.fullscreenElement) {
        elem.requestFullscreen().then(() => {
          isFullscreen.value = true
          fileKey.value++ // Forzar re-render del PDF
        }).catch(err => {
          console.error(`Error al intentar pantalla completa: ${err.message}`)
        })
      } else {
        document.exitFullscreen().finally(() => {
          isFullscreen.value = false
          fileKey.value++ // Forzar re-render del PDF
        })
      }
    }

    // Listener para detectar cuando se sale de pantalla completa con ESC
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement) {
        isFullscreen.value = false
        fileKey.value++ // Forzar re-render del PDF
      }
    }

    onMounted(() => {
      document.addEventListener('fullscreenchange', handleFullscreenChange)
    })

    // Funciones de pestañas
    const seleccionarOtros = async (idx) => {
      tabActiva.value = 3
      await abrirSubTab(idx)
      
      // Cerrar el dropdown de Bootstrap
      if (dropdownOtros.value) {
        const dropdownToggle = dropdownOtros.value.querySelector('.dropdown-toggle')
        if (dropdownToggle) {
          // Intentar usar Bootstrap 5 API
          try {
            const bootstrap = window.bootstrap
            if (bootstrap && bootstrap.Dropdown) {
              const dropdownInstance = bootstrap.Dropdown.getInstance(dropdownToggle)
              if (dropdownInstance) {
                dropdownInstance.hide()
              } else {
                // Si no hay instancia, crear una y cerrarla
                const newInstance = new bootstrap.Dropdown(dropdownToggle)
                newInstance.hide()
              }
            } else {
              // Fallback: remover las clases 'show' manualmente
              dropdownOtros.value.classList.remove('show')
              const menu = dropdownOtros.value.querySelector('.dropdown-menu')
              if (menu) {
                menu.classList.remove('show')
              }
              dropdownToggle.setAttribute('aria-expanded', 'false')
            }
          } catch (error) {
            // Fallback manual si hay error
            dropdownOtros.value.classList.remove('show')
            const menu = dropdownOtros.value.querySelector('.dropdown-menu')
            if (menu) {
              menu.classList.remove('show')
            }
            if (dropdownToggle) {
              dropdownToggle.setAttribute('aria-expanded', 'false')
            }
          }
        }
      }
    }

    const abrirSubTab = async (idx) => {
      if (idx === 2 && props.esquema.versionado != 1) return
      if (idx === 1 && props.esquema.ocr != 1) return
      if (idx === 3 && props.esquema.documentos_relacionados != 1) return
      if (idx === 4 && props.esquema.firmar_documentos != 1) return
      if (idx === 5 && props.esquema.firmar_documentos_vidsigner != 1) return
      subTabActiva.value = idx
      if (idx === 1) {
        await abrirOcrTab()
      } else if (idx === 2) {
        await cargarVersiones()
      } else if (idx === 3) {
        await cargarRelacionados()
      } else if (idx === 0) {
        await cargarLogs()
      }
    }

    onBeforeUnmount(() => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      clearListOutsideClick()
    })

    const abrirOcrTab = async () => {
      if (props.esquema.ocr != 1) return
      tabActiva.value = 3
      subTabActiva.value = 1
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) {
        textoOcr.value = 'No hay documento seleccionado.'
        return
      }
      cargandoOcr.value = true
      textoOcr.value = ''
      try {
        const esquemaId = props.id || route.params?.id;
        const response = await axiosInstance.get(`api/ocrRegistro/${esquemaId}/${props.registroSeleccionado.value.id}`)
        textoOcr.value = response.data || 'Sin texto OCR disponible.'
      } catch (error) {
        textoOcr.value = 'Error al obtener el texto OCR.'
      } finally {
        cargandoOcr.value = false
      }
    }

    const cargarVersiones = async () => {
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) return
      cargandoVersiones.value = true
      try {
        const esquemaId = props.id || route.params?.id;
        const response = await axiosInstance.get(`api/versionesRegistro/${esquemaId}/${props.registroSeleccionado.value.id}`)
        versiones.value = response.data || []
      } catch (e) {
        versiones.value = []
      } finally {
        cargandoVersiones.value = false
      }
    }

    const descargarVersion = (ver) => {
      if (!ver || !ver.base64 || !ver.nombre) return
      const mime = obtenerMimeDesdeExtension(ver.nombre)
      const enlace = document.createElement('a')
      enlace.href = `data:${mime};base64,` + ver.base64
      enlace.download = ver.nombre
      document.body.appendChild(enlace)
      enlace.click()
      document.body.removeChild(enlace)
    }

    // Cargar lista de esquemas disponibles
    const cargarEsquemas = async () => {
      try {
        const response = await axiosInstance.get('/api/esquemas')
        esquemasDisponibles.value = response.data.esquemas || []
      } catch (e) {
        console.error('Error al cargar esquemas:', e)
        esquemasDisponibles.value = []
      }
    }

    // Obtener nombre del esquema por ID
    const getEsquemaNombre = (esquemaId) => {
      if (!esquemaId) return ''
      
      const esquema = esquemasDisponibles.value.find(e => e.id === esquemaId)
      return esquema ? esquema.nombre : `Esquema ${esquemaId}`
    }

    const cargarRelacionados = async () => {
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) return
      cargandoRelacionados.value = true
      try {
        const esquemaId = props.id || route.params?.id;
        const response = await axiosInstance.get(`api/documentosRelacionados/${esquemaId}/${props.registroSeleccionado.value.id}`)
        documentosRelacionados.value = response.data.data || []
      } catch (e) {
        documentosRelacionados.value = []
      } finally {
        cargandoRelacionados.value = false
      }
    }

    const buscarDocumentos = async (page = 1) => {
      cargandoBusqueda.value = true
      try {
        const params = { 
          q: busquedaTermino.value, 
          page, 
          per_page: busquedaPaginacion.value.per_page 
        }
        // Agregar esquema_buscar si está seleccionado
        if (esquemaBuscar.value) {
          params.esquema_buscar = esquemaBuscar.value
        }
        const esquemaId = props.id || route.params?.id;
        const response = await axiosInstance.get(`api/buscarDocumentos/${esquemaId}/${props.registroSeleccionado.value.id}`, { params })
        resultadosBusqueda.value = response.data.data || response.data
        busquedaPaginacion.value = response.data.pagination || { page, per_page: busquedaPaginacion.value.per_page, total: 0 }
      } catch (e) {
        resultadosBusqueda.value = []
        busquedaPaginacion.value = { page, per_page: busquedaPaginacion.value.per_page, total: 0 }
      } finally {
        cargandoBusqueda.value = false
      }
    }

    const relacionarDocumento = async (doc) => {
      if (!doc || !props.registroSeleccionado.value?.id) return
      relacionando.value = true
      try {
        const esquemaId = props.id || route.params?.id;
        const body = { 
          documento_id: doc.id,
          esquema_rel_id: doc.esquema_id || esquemaId
        }
        await axiosInstance.post(`api/relacionarDocumento/${esquemaId}/${props.registroSeleccionado.value.id}`, body)
        await cargarRelacionados()
        mostrarModalRelacionar.value = false
        resultadosBusqueda.value = []
        busquedaTermino.value = ''
      } catch (e) {
        // noop
      } finally {
        relacionando.value = false
      }
    }

    const irAVerDocumentoRelacionado = (doc) => {
      if (!doc || !doc.id) return
      // Usar esquema_id del documento si está disponible, sino usar el esquema actual
      const esquemaIdDoc = doc.esquema_id || (props.id || route.params?.id)
      const url = `/esquema/${esquemaIdDoc}/registro/${doc.id}`
      window.open(url, '_blank', 'noopener,noreferrer')
    }

    const descargarDocumentoRelacionado = async (doc) => {
      if (!doc || !doc.id) return
      try {
        // Usar esquema_id del documento si está disponible, sino usar el esquema actual
        const esquemaIdDoc = doc.esquema_id || (props.id || route.params?.id)
        const response = await axiosInstance.get(`api/descargarRelacionado/${esquemaIdDoc}/${doc.id}`)
        const nombre = response.data?.nombre || doc.nombre || 'documento'
        const mime = obtenerMimeDesdeExtension(nombre)
        const enlace = document.createElement('a')
        enlace.href = `data:${mime};base64,` + response.data.base64
        enlace.download = nombre
        document.body.appendChild(enlace)
        enlace.click()
        document.body.removeChild(enlace)
      } catch (e) {
        console.log(e)
      }
    }

    const quitarDocumentoRelacionado = async (doc) => {
      if (!doc || !doc.id || !props.registroSeleccionado.value?.id) return
      try {
        const esquemaId = props.id || route.params?.id;
        // Incluir esquema_id del documento en el body si está disponible
        const body = { documento_id: doc.id }
        if (doc.esquema_id) {
          body.esquema_id = doc.esquema_id
        }
        await axiosInstance.post(`api/quitarRelacionado/${esquemaId}/${props.registroSeleccionado.value.id}`, body)
        await cargarRelacionados()
      } catch (e) {
        console.log(e)
      }
    }

    const cerrarRelacionar = () => {
      mostrarModalRelacionar.value = false
      resultadosBusqueda.value = []
      busquedaTermino.value = ''
      esquemaBuscar.value = null // Resetear filtro de esquema
    }

    const cargarLogs = async () => {
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) return
      cargandoLogs.value = true
      try {
        const esquemaId = props.id || route.params?.id;
        const response = await axiosInstance.get(`api/logRegistro/${esquemaId}/${props.registroSeleccionado.value.id}`)
        logs.value = response.data.data || []
      } catch (e) {
        logs.value = []
      } finally {
        cargandoLogs.value = false
      }
    }

    // Funciones de reemplazo de documento
    const onFileChangeRemplazar = (e) => {
      const file = e.target.files[0]
      if (!file) {
        fileRemplazo.value = null
        fileRemplazoBase64.value = ""
        return
      }
      const reader = new FileReader()
      reader.onload = (event) => {
        fileRemplazoBase64.value = event.target.result.split(",")[1]
        fileRemplazo.value = file
      }
      reader.readAsDataURL(file)
    }

    const cancelarRemplazar = () => {
      mostrarModalRemplazar.value = false
      fileRemplazo.value = null
      fileRemplazoBase64.value = ""
    }

    const guardarRemplazo = async () => {
      if (!fileRemplazo.value || !fileRemplazoBase64.value) return
      try {
        const esquemaId = props.id || route.params?.id;
        await axiosInstance.post(`api/remplazarDocumento/${esquemaId}/${props.registroSeleccionado.value.id}`, {
          archivo: fileRemplazoBase64.value,
          nombre: fileRemplazo.value.name,
          tipo: fileRemplazo.value.type
        })
        mostrarModalRemplazar.value = false
        fileRemplazo.value = null
        fileRemplazoBase64.value = ""
        props.showNotification('Documento reemplazado correctamente.', 'alert-success')
        
        // Actualizar la extensión del registro seleccionado
        const nuevaExtension = (fileRemplazo.value?.name || '').split('.').pop()?.toLowerCase()
        if (nuevaExtension) {
          emit('actualizar-registro', { ...props.registroSeleccionado.value, extension: nuevaExtension })
        }
      } catch (error) {
        props.showNotification('Error al reemplazar el documento.', 'alert-danger')
      }
    }

    const generarDesdePlantilla = async () => {
      if (!props.registroSeleccionado.value || !props.registroSeleccionado.value.id) {
        props.showNotification('No hay registro seleccionado.', 'alert-warning')
        return
      }

      if (!confirm('¿Desea generar un nuevo documento desde la plantilla? Esto ' + 
        (props.esquema.versionado == 1 ? 'creará una nueva versión' : 'reemplazará el documento actual') + '.')) {
        return
      }

      generandoDocumento.value = true
      try {
        const esquemaId = props.id || route.params?.id
        const response = await axiosInstance.post(
          `api/generarDocumentoDesdePlantilla/${esquemaId}/${props.registroSeleccionado.value.id}`
        )

        if (response.data.success) {
          props.showNotification('Documento generado correctamente desde la plantilla.', 'alert-success')
          // Recargar el registro para mostrar el nuevo documento
          emit('actualizar-registro', { ...props.registroSeleccionado.value })
          // Forzar recarga del archivo
          fileKey.value++
        } else {
          props.showNotification(response.data.error || 'Error al generar el documento.', 'alert-danger')
        }
      } catch (error) {
        const mensaje = error.response?.data?.error || 'Error al generar el documento desde la plantilla.'
        props.showNotification(mensaje, 'alert-danger')
      } finally {
        generandoDocumento.value = false
      }
    }


    // Watch para cargar datos cuando cambia el registro seleccionado o el esquema
    let registroIdAnterior = null
    watch([() => props.registroSeleccionado.value?.id, () => props.esquema], async ([nuevoId, nuevoEsquema]) => {
      const nuevoRegistro = props.registroSeleccionado.value
      if (nuevoRegistro != null && nuevoId && nuevoEsquema && nuevoEsquema.propiedades) {
        // Solo resetear a la primera pestaña y recargar cuando cambia el ID del registro (nuevo registro seleccionado)
        // No resetear si solo cambian las propiedades del mismo registro
        if (registroIdAnterior !== nuevoId) {
          tabActiva.value = props.hideDocumentTab ? 1 : 0
          subTabActiva.value = 0
          registroIdAnterior = nuevoId
          aprobacionInfo.value = null
          permisosAprobacionCargadosEnAcciones.value = false
          
          if (detalleDocumento.value) {
            detalleDocumento.value.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }

          const extension = nuevoRegistro.extension

          // Limpiar antes de cargar
          fileUrl.value = null
          fileType.value = null
          filename.value = ''
          imgbase64.value = ''
          textContent.value = ''
          docxSrc.value = ''
          excelSrc.value = ''
          pptxSrc.value = ''
          pdfSrc.value = ''
          dwgBase64.value = ''
          
          // Usar props.id si está disponible, sino usar route.params?.id como fallback
          const esquemaId = props.id || route.params?.id;
          
          if (!esquemaId) {
            console.error('Error: No se pudo obtener el esquemaId ni desde props ni desde la ruta');
            return;
          }

          // Activar indicador de carga
          cargandoArchivo.value = true;
          
          try {
            const response = await axiosInstance.get(`api/ficheroRegistro/${esquemaId}/${nuevoRegistro.id}`)

            filename.value = response.data.filename || nuevoRegistro.nombre || 'documento'
            fileType.value = getFileTypeFromExtension(extension || '')

            fileUrl.value = response.data.base64

            if (fileType.value === 'image') {
              imgbase64.value = `data:image/${extension.toLowerCase()};base64,${response.data.base64}`
            }

            // prepare viewers
            if (fileType.value === 'text') {
              try {
                const array = base64ToUint8Array(response.data.base64)
                const decoder = new TextDecoder('utf-8')
                textContent.value = decoder.decode(array)
              } catch (e) {
                try { textContent.value = atob(response.data.base64) } catch (_) { textContent.value = 'Error al mostrar texto.' }
              }
            } else if (fileType.value === 'pdf') {
              pdfSrc.value = base64ToArrayBuffer(response.data.base64)
            } else if (fileType.value === 'docx') {
              docxSrc.value = base64ToArrayBuffer(response.data.base64)
            } else if (fileType.value === 'xlsx') {
              excelSrc.value = base64ToArrayBuffer(response.data.base64)
            } else if (fileType.value === 'pptx') {
              pptxSrc.value = base64ToArrayBuffer(response.data.base64)
            } else if (fileType.value === 'dwg') {
              // Guardar el base64 para inicializar cuando el contenedor esté disponible
              dwgBase64.value = response.data.base64
              // Asegurar que la pestaña de documento esté activa
              tabActiva.value = 0
            }

            fileKey.value++
          } catch (error) {
            console.error('Error al cargar el archivo:', error);
            props.showNotification('Error al cargar el archivo', 'alert-danger');
          } finally {
            // Desactivar indicador de carga
            cargandoArchivo.value = false;
          }
        }
      }
    }, { immediate: true })

    // Watch para búsqueda automática
    watch(mostrarModalRelacionar, (val) => {
      if (val) {
        cargarEsquemas() // Cargar esquemas al abrir el modal
        buscarDocumentos(1)
      }
    }, { immediate: true })

    // Computed para propiedad de caducidad
    const propiedadCaducidad = computed(() => {
      if (!props.esquema.tiene_caducidad || !props.esquema.propiedad_caducidad_id) {
        return null
      }
      return props.esquema.propiedades?.find(p => p.id === props.esquema.propiedad_caducidad_id)
    })

    // Computed para días restantes de caducidad
    const diasRestantesCaducidad = computed(() => {
      if (!propiedadCaducidad.value || !props.registroSeleccionado.value) {
        return null
      }
      const fechaCaducidad = props.registroSeleccionado.value[propiedadCaducidad.value.campoBD]
      if (!fechaCaducidad) return null
      
      const hoy = new Date()
      hoy.setHours(0, 0, 0, 0)
      const caducidad = new Date(fechaCaducidad)
      caducidad.setHours(0, 0, 0, 0)
      
      const diffTime = caducidad - hoy
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
      return diffDays
    })

    // Función para formatear fecha (solo fecha, sin hora)
    const formatDate = (dateString) => {
      if (!dateString) return null
      const date = new Date(dateString)
      return date.toLocaleDateString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit' })
    }

    // Cargar esquemas al montar el componente
    onMounted(() => {
      cargarEsquemas()
    })

    return {
      // Variables reactivas
      route,
      tabActiva,
      subTabActiva,
      detalleDocumento,
      fullscreenDiv,
      isFullscreen,
      fileKey,
      fileUrl,
      fileType,
      filename,
      imgbase64,
      textContent,
      docxSrc,
      excelSrc,
      pptxSrc,
      pdfSrc,
      dwgBase64,
      mostrarModalEliminar,
      mostrarModalRemplazar,
      mostrarModalRelacionar,
      fileRemplazo,
      fileRemplazoBase64,
      cargandoGenerarContenido,
      cargandoOcr,
      cargandoVersiones,
      cargandoRelacionados,
      cargandoBusqueda,
      cargandoLogs,
      cargandoArchivo,
      relacionando,
      generandoDocumento,
      textoOcr,
      versiones,
      documentosRelacionados,
      resultadosBusqueda,
      busquedaTermino,
      busquedaPaginacion,
      logs,
      esquemasDisponibles,
      esquemaBuscar,
      
      // Computed properties
      esquemaId,
      aprobacionPendiente,
      hayAcciones,
      propiedadesAgrupadas,
      esquemasParaRelacionar,
      mostrarFiltroEsquema,
      propiedadCaducidad,
      diasRestantesCaducidad,
      
    // Funciones
      formatearNombrePropiedad,
      formatDecimal,
      formatDateTime,
      obtenerValoresLista,
      valoresListaFiltrados,
      toggleListDropdown,
      selectValorLista,
      openListCampo,
      listFilterText,
      getFileIcon,
      getFileIconAlt,
      updateField,
      updateDecimal,
      guardarRegistro,
      confirmarEliminar,
      accionGenerarContenido,
      copiarEnlace,
      abrirDocumentoEnNuevaPestana,
      descargarArchivo,
      pantallaCompleta,
      seleccionarOtros,
      abrirSubTab,
      abrirOcrTab,
      cargarVersiones,
      descargarVersion,
      cargarRelacionados,
      buscarDocumentos,
      relacionarDocumento,
      irAVerDocumentoRelacionado,
      descargarDocumentoRelacionado,
      quitarDocumentoRelacionado,
      cerrarRelacionar,
      cargarEsquemas,
      getEsquemaNombre,
      cargarLogs,
      onFileChangeRemplazar,
      cancelarRemplazar,
      guardarRemplazo,
      generarDesdePlantilla,
      handleActualizarRegistro,
      formatDate,
      mostrarModalRechazarDetalle,
      motivoRechazoDetalle,
      procesandoAprobacionDetalle,
      aprobarDesdeDetalle,
      abrirModalRechazarDetalle,
      rechazarDesdeDetalle,
      irATabAcciones
    }
  }
}
</script>

<style scoped>
/* Dropdown de listas: evitar que el accordion (overflow) recorte el desplegable */
.accordion-dropdown-open .accordion-item {
  overflow: visible !important;
}
.accordion-dropdown-open .accordion-collapse {
  overflow: visible !important;
}
.accordion-dropdown-open .accordion-body {
  overflow: visible !important;
}
.list-dropdown-open {
  z-index: 1100;
  position: relative;
}

/* Modal de relacionar documento - 50% más ancho que modal-lg */
.modal-relacionar {
  max-width: calc(800px * 1.5) !important;
  width: calc(800px * 1.5) !important;
}

@media (max-width: 1200px) {
  .modal-relacionar {
    max-width: 95vw !important;
    width: 95vw !important;
  }
}
.pdf-container {
  height: 70vh;
  width: 100%;
  overflow: auto;
  border: 1px solid #ccc;
  position: relative;
}

.pdf-container-completa {
  height: 100vh;
  width: 100vw;
  overflow: auto;
  border: none;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  background: white;
}

.pptx-container {
  height: 70vh;
  width: 100%;
  overflow: auto;
  border: 1px solid #ccc;
  position: relative;
}

.pptx-container-completa {
  height: 100vh;
  width: 100vw;
  overflow: auto;
  border: none;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  background: white;
}

.dwg-container {
  height: 70vh;
  width: 100%;
  overflow: hidden;
  border: 1px solid #ccc;
  position: relative;
}

.dwg-container-completa {
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  border: none;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
  background: white;
}

.image-container img {
  max-width: 100%;
  height: auto;
}

.file-container {
  margin-top: 16px;
}

.nav-tabs-nowrap {
  flex-wrap: nowrap !important;
  overflow-x: auto;
  white-space: nowrap;
}

.nav-tabs-nowrap .nav-item {
  float: none;
  display: inline-block;
}

.nav-tabs {
  overflow-y: visible !important;
}

.ocr-content-scroll {
  max-height: 70vh;
  overflow-y: auto;
  background: #f8f9fa;
  border-radius: 6px;
  padding: 1rem;
}

.celda-ellipsis {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subtabs-bottom {
  border-top: 1px solid #dee2e6;
  border-bottom: none;
}

.subtabs-bottom .nav-link {
  border: 1px solid transparent;
  border-top-left-radius: .25rem;
  border-top-right-radius: .25rem;
  border-bottom: none;
  color: #5e72e4;
}

.subtabs-bottom .nav-link:hover {
  border-color: transparent;
}

.subtabs-bottom .nav-link.active {
  color: #344767;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 transparent;
  border-bottom: 1px solid #fff;
}

.card-header {
  position: relative;
  z-index: auto;
  overflow: visible !important;
}

.card-body {
  position: relative;
  z-index: 1;
}

.nav-tabs {
  overflow: visible !important;
}

.dropdown-menu {
  position: absolute;
  z-index: 1055;
}

.accordion-item {
  border: 1px solid #dee2e6;
  margin-bottom: 8px;
  border-radius: 0.375rem;
  overflow: hidden;
}

.accordion-item:last-child {
  margin-bottom: 0;
}

.accordion-button {
  background-color: #f8f9fa;
  border: none;
  border-radius: 0.375rem;
  font-weight: 500;
  color: #495057;
  padding: 1rem 1.25rem;
}

.accordion-button:not(.collapsed) {
  background-color: #e9ecef;
  color: #212529;
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
}

.accordion-button:focus {
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
  border-color: #86b7fe;
}

.accordion-button::after {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23212529'%3e%3cpath fill-rule='evenodd' d='M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e");
  transition: transform 0.2s ease-in-out;
  width: 1.25rem;
  height: 1.25rem;
  margin-left: auto;
}

.accordion-button:not(.collapsed)::after {
  transform: rotate(180deg);
}

.accordion-body {
  padding: 1.25rem;
  background-color: #fff;
}

.accordion-collapse {
  border-top: 1px solid #dee2e6;
}

/* Botones más pequeños para documentos relacionados */
.btn-xs {
  padding: 0.15rem 0.4rem;
  font-size: 0.75rem;
  line-height: 1.2;
  border-radius: 0.2rem;
}

/* Estados de aprobación sin degradado */
.estado-aprobacion {
  padding: 0.75rem 1rem;
  border-radius: 0.375rem;
  border: 1px solid;
  background-color: transparent;
}

.estado-aprobado {
  border-color: #198754;
  color: #198754;
  background-color: #f8fff9;
}

.estado-rechazado {
  border-color: #dc3545;
  color: #dc3545;
  background-color: #fff8f8;
}

.estado-pendiente {
  border-color: #ffc107;
  color: #856404;
  background-color: #fffef8;
}
</style>
